import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ControlContainer, ControlValueAccessor, FormGroup } from '@angular/forms';
import { SubSink } from 'subsink';
import {
  tap,
  first,
  pairwise,
  map,
  distinct,
  finalize,
  concatMap,
} from 'rxjs/operators';
import { GroupedOption } from '@app/run-planning/model/option';
import { ComboBoxComponent } from '@app/cloud-run-prep/directives/combobox/combobox.component';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { IDynamicLoadingComponent } from '@app/run-planning/interface';
import { NUM_DROPDOWN_OPTIONS_LIMIT } from '@app/run-planning/constants';
import { HelperService } from '@app/run-planning/services/helper.service';
import { ReferenceFilesService } from '@stratus/gss-ng-sdk';
import { IReferenceFileFieldSettings } from '@app/run-planning/model/form-field';
import { ReferenceFileUploadOrchestratorService } from '@app/shared/services/reference-file-upload-orchestrator.service';

@Component({
  selector: 'app-reference-file-dropdown',
  templateUrl: './reference-file-dropdown.component.html',
  styleUrls: ['./reference-file-dropdown.component.scss']
})
export class ReferenceFileDropdownComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor, IDynamicLoadingComponent {
  /* Reference File form control Id in the formGroup */
  @Input() referenceFileFormControlName: string;
  @Input() referenceFileFieldSettings: IReferenceFileFieldSettings = {};
  /* Analysis form field settings field used as filter parameter to fetch the options */
  @Input() referenceFileGenomeFilterFieldId: string;
  @Input() genomeFilterGenomeId: string;
  /* Analysis form field settings field used as filter parameter to fetch the options */
  @Input() referenceFileTypeFilter: string;
  /* Hash table version of genomes allowed for the selected application, defined in AVD return from render call */
  @Input() genomeHashTableVersion: string;
  @Input() analysisVersionDefinitionId: string;
  /* Analysis form field required field, if optional, options will include clear field */
  @Input() isRequired = false;
  @Input() fieldLabel = 'Reference File';
  @Input() disableReferenceFileUpload: boolean;
  referenceFileOptions: GroupedOption[] = [];

  @Input() newReferenceFileName$: Observable<string>;
  @Output() newReferenceFileAdded = new EventEmitter<{
    formControlName: string,
    fileName: string,
    referenceFileId: string,
    isMultiValue: boolean,
  }>();

  private loadingSubject = new BehaviorSubject<boolean>(true);
  public isLoading$ = this.loadingSubject.asObservable();

  @ViewChild('referenceFileSelectCombobox', { static: true })
  referenceFileSelectCombobox: ComboBoxComponent;

  private depedentGenomeIdSubject = new BehaviorSubject<string>('');
  private dependentGenomeId$: Observable<string> = this.depedentGenomeIdSubject.asObservable();

  get parentFormGroup(): FormGroup { return this.controlContainer.control as FormGroup; }
  get refFileFormControl() { return this.parentFormGroup.get(this.referenceFileFormControlName); }

  private subs = new SubSink();
  private cachedValue;

  constructor(
    private controlContainer: ControlContainer,
    private helper: HelperService,
    private referenceFilesService: ReferenceFilesService,
    private uploadOrchestrator: ReferenceFileUploadOrchestratorService,
  ) { }

  ngOnInit() {
    // Initialize dropdown options
    this.referenceFileOptions = this.getEmptyOptions();
    this.subscribeToOptionsDependencies();

    this.subs.sink = this.isLoading$.pipe(pairwise()).subscribe(([preIsLoading, currentIsLoading]) => {
      if (preIsLoading != currentIsLoading) {
        // Only cache/refetch the value when loading status changed
        if (currentIsLoading) {
          // To cache the value while it begins to load
          this.cachedValue = this.refFileFormControl.value;
          this.clearSelection();
        } else if (this.cachedValue) {
          // If have any cached value, patch back and clear the cache
          let cachedValue = this.cachedValue;
          if (this.referenceFileFieldSettings.isMultiSelect) {
            this.refFileFormControl.patchValue([...cachedValue, ...(this.refFileFormControl.value || [])]);
          } else {
            this.refFileFormControl.patchValue(this.cachedValue);
          }
          this.cachedValue = undefined;
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hasOwnProperty('genomeFilterGenomeId')) {
      this.depedentGenomeIdSubject.next(changes.genomeFilterGenomeId.currentValue);
    }
  }

  /**
   * Fetch the options list whenever the specified genomeFilterField value changes
   * and/or parent dynamic-settings-form component request for re-update due to new reference file addded
   */
  private subscribeToOptionsDependencies() {
    /**
     * Notes on the 2 observables to combine:
     *
     * `this.dependentGenomeId$`: When a different genome value is selected on the dependent field
     * `this.newReferenceFileName$`: When a new reference file is added via popup modal,
     *    emits the filename of the added file to all reference file dropdowns.
     * `this.referenceFileGenomeFilterFieldId` is null if this field has no dependency on a genome.
     *    --> If null, then just getOptions$ without genome id
     *    --> If not null, but genomeId returned is null, then return empty options (as per previous implementation)
     */
    this.subs.sink = combineLatest(this.dependentGenomeId$, this.newReferenceFileName$)
      .pipe(
        distinct(),
        tap(() => this.loadingSubject.next(true)),
        concatMap(([genomeId, _]) => {
          if (!this.referenceFileGenomeFilterFieldId) {
            // This field has no genome dependency
            return this.getOptions$(null, this.referenceFileTypeFilter);
          } else {
            // Only getOptions when user has selected the dependent genome
            return genomeId ? this.getOptions$(genomeId, this.referenceFileTypeFilter) : of(this.getEmptyOptions());
          }
        }),
        finalize(() => this.loadingSubject.next(false)),
      )
      .subscribe({
        next: groupedOptions => {
          this.referenceFileOptions = groupedOptions;
          this.loadingSubject.next(false);
        },
        error: error => console.error(error),
      });
  }

  registerOnChange(fn: any): void {
  }

  registerOnTouched(fn: any): void {
  }

  writeValue(obj: any): void {
  }

  clearSelection() {
    this.controlContainer.control.get(this.referenceFileFormControlName).patchValue(null);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private getOptions$(genomeId: string, fileType: string) {
    const requestParams = {
      genomeId,
      type: fileType,
      sort: 'displayName',
      pageSize: NUM_DROPDOWN_OPTIONS_LIMIT
    };
    return this.referenceFilesService.listReferenceFiles(requestParams)
      .pipe(
        map(pagedItems => pagedItems.items.map(x => this.helper.mapToCustomOption(x))),
        first(),
        map(allCustomOptions => this.helper.groupByCustomAndStandardOptions(allCustomOptions, 'Reference Files'))
      );
  }

  private getEmptyOptions() {
    return this.helper.groupByCustomAndStandardOptions([], 'Reference Files');
  }

  /**
   * The page will be blocked by
   * - loading spinner during
   *    1. filename validation
   *    2. gettting existing file with the same name
   * - progress modal during file upload
   */
  async handleAddNewOption(event) {
    const importedFile = (event.target as HTMLInputElement).files[0];
    if (!importedFile) {
      event.target.value = '';
      return;
    }

    try {
      const result = await this.uploadOrchestrator.uploadReferenceFile(importedFile, {
        fileType: this.referenceFileTypeFilter,
        genomeFilterGenomeId: this.genomeFilterGenomeId,
        genomeHashTableVersion: this.genomeHashTableVersion,
        analysisVersionDefinitionId: this.analysisVersionDefinitionId,
      });

      if (result) {
        this.newReferenceFileAdded.emit({
          formControlName: this.referenceFileFormControlName,
          fileName: result.fileName,
          referenceFileId: result.referenceFileId,
          isMultiValue: this.referenceFileFieldSettings.isMultiSelect,
        });
      }
    } finally {
      event.target.value = '';
    }
  }
}
